
import { useEffect, useRef, useState } from "react"
import { Badge, Container, Card, OverlayTrigger, Tooltip } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { toast } from '../../common/Toast/toast';
import Swal from "sweetalert2"
import { confirm } from "../../common/Confirm/confirm";
import { Eye, Edit, Trash2 } from "lucide-react"

import { getJobs, deleteJob, getJobsByStatus, getJobById } from "../../../../services/JobsService.js"
import { useAuth } from "../../../../context/AuthContext"
import Datatable from "../../common/CustomDataTable.jsx"

const ListJobs = () => {
  const navigate = useNavigate()

  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const searchTerm = ""
  const statusFilter= ""
  const [currentPage, setCurrentPage] = useState(1)

  const itemsPerPage = 10

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  
  useEffect(() => {
    if (!hasPermission("list-jobs", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission view jobs.");
        navigate("/");
        console.log("hello");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const fetchJobs = async (page = 1, search = "", status = "") => {
    try {
      setLoading(true)

      let response
      if (status) {
        response = await getJobsByStatus(status, itemsPerPage, page)
      } else {
        response = await getJobs(itemsPerPage, page, search)
      }

      const transformedJobs = (response.data || []).map((job, index) => ({
        ...job,
        srno: (page - 1) * itemsPerPage + index + 1,
        displayStatus: job.status?.toLowerCase() === "inactive" ? "inactive" : "active",
        postedDate: job.jobPostedDate || job.createdAt,
      }))

      setJobs(transformedJobs)
    } catch (error) {
      console.error("[ListJobs] Error fetching jobs:", error)
      toast.error(`Failed to fetch jobs: ${error.message}`)
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs(currentPage, searchTerm, statusFilter)
  }, [currentPage, searchTerm, statusFilter])



  const handleDelete = async (jobId, jobTitle) => {
    const result = await confirm({
      title: "Are you sure?",
      text: `You want to delete "${jobTitle}"?`,
      tone: 'danger',
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        const response = await deleteJob(jobId)
        toast.success(response.message?.message || "Job deleted successfully!")
        fetchJobs(currentPage, searchTerm, statusFilter)
      } catch (error) {
        console.error("[ListJobs] Error deleting job:", error)
        toast.error(`Failed to delete job: ${error.message}`)
      }
    }
  }

  const handleView = async (jobId) => {
    try {
      const response = await getJobById(jobId)
      const job = response.data

      const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      }

      let html = `
        <div class="text-start">
          <div class="mb-3">
            <h5 class="fw-bold">${job.jobName}</h5>
            <hr class="my-2" />
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Company:</strong></p>
              <p class="text-black">${job.organizationName}</p>
            </div>
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Location:</strong></p>
              <p class="text-black">${job.jobLocation}</p>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Salary:</strong></p>
              <p class="text-black">${job.salary}</p>
            </div>
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Posted Date:</strong></p>
              <p class="text-black">${formatDate(job.jobPostedDate || job.createdAt)}</p>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Status:</strong></p>
              <p class="text-black"><span class="badge bg-${getStatusBadgeVariant(job.status)}">${(job.status || "ACTIVE").toUpperCase()}</span></p>
            </div>
            <div class="col-md-6">
              <p class="mb-1 text-dark"><strong>Position Status:</strong></p>
              <p class="text-black">${job.jobPositionStatus?.toUpperCase() || "OPEN"}</p>
            </div>
          </div>
        </div>
      `

      await Swal.fire({
        html,
        confirmButtonColor: "#007bff",
        confirmButtonText: "Close",
        width: "800px",
      })
    } catch (error) {
      console.error("[ListJobs] Error fetching job details for view:", error)
      toast.error("Failed to fetch job details")
    }
  }

  const getStatusBadgeVariant = (status) => {
    if (status?.toLowerCase() === "inactive") {
      return "danger"
    }
    return "success"
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const headers = [
    {
      title: "Sr. No",
      prop: "srno",
      cell: (row) => <span className="text-muted">{row.srno}</span>,
    },
    {
      title: "Job Title",
      prop: "jobName",
      cell: (row) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => handleView(row.id)}
          title="Click to view job details"
          onMouseOver={(e) => {
            e.currentTarget.querySelector("span").style.textDecoration = "underline";
          }}
          onMouseOut={(e) => {
            e.currentTarget.querySelector("span").style.textDecoration = "none";
          }}
        >
          <span
            style={{
              color: "#0000FF"
            }}
            className="fw-semibold"
          >
            {row.jobName}
          </span>
          <div className="text-muted small">{row.organizationName}</div>
        </div>
      ),
    },
    {
      title: "Location & Position",
      prop: "jobLocation",
      cell: (row) => (
        <div>
          <div className="text-nowrap">{row.jobLocation}</div>
          <div className="d-flex gap-1 align-items-center mt-1">
            <Badge bg="secondary" className="small">
              {row.jobPositionStatus?.toUpperCase() || "OPEN"}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      title: "Salary",
      prop: "salary",
      cell: (row) => (
        <span className="fw-semibold text-success">{row.salary}</span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      cell: (row) => (
        <Badge bg={getStatusBadgeVariant(row.displayStatus || row.status)}>
          {row.displayStatus?.toUpperCase() || "ACTIVE"}
        </Badge>
      ),
    },
    {
      title: "Posted Date",
      prop: "jobPostedDate",
      cell: (row) => (
        <div className="text-nowrap">
          <div>{formatDate(row.jobPostedDate || row.createdAt)}</div>
        </div>
      ),
    },
    {
      title: "Actions",
      prop: "action",
      cell: (row) => (
        <div className="d-flex gap-2">
          {hasPermission("list-jobs", "view", "lms") && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="tooltip-sm">View Details</Tooltip>}
            >
              <button
                className="btn btn-success btn-xs sharp me-2"
                onClick={() => handleView(row.id)}
              >
                <Eye size={18} />
              </button>
            </OverlayTrigger>
          )}
          {hasPermission("list-jobs", "update", "lms") && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="tooltip-sm">Edit</Tooltip>}
            >
              <button
                className="btn btn-warning btn-xs sharp me-2"
                onClick={() => navigate(`/edit/${row.id}`)}
              >
                <Edit size={18} />
              </button>
            </OverlayTrigger>
          )}
          {hasPermission("list-jobs", "delete", "lms") && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="tooltip-sm">Delete</Tooltip>}
            >
              <button
                className="btn btn-danger btn-xs sharp me-2"
                onClick={() => handleDelete(row.id, row.jobName)}
              >
                <Trash2 size={18} />
              </button>
            </OverlayTrigger>
          )}
        </div>
      ),
    },
  ]

  return (
    <>      <Container fluid className="py-4">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Datatable
              data={jobs}
              headers={headers}
              currentPage={currentPage}
              recordsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              addButtonText="Post New Job"
              addButtonLink={
                hasPermission("list-jobs", "add", "lms") ? "/post-jobs" : undefined
              }
              pageTitle="Jobs Management"
              loading={loading}
            />
          </Card.Body>
        </Card>
      </Container>
    </>
  )
}

export default ListJobs
